import { useState, useRef, useEffect } from 'react';
import { Music, Sparkles, Lock, ArrowLeft, Wand2, Download, Loader2, CheckCircle, XCircle, Play, Pause, Volume2 } from 'lucide-react';
import axios from 'axios';

const AudioGenerator = () => {
  // ✅ NOUVEAU: État d'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    prompt: '',
    style: 'Pop',
    customStyle: '',
    useCustomStyle: false,
    title: '',
    customMode: true,
    instrumental: false,
    model: 'V5'
  });
  const [isPlaying, setIsPlaying] = useState({});
  
  // ✅ NOUVEAU: Tracking backend
  const [generationId, setGenerationId] = useState(null);
  const [generationStartTime, setGenerationStartTime] = useState(null);
  
  const audioRefs = useRef({});
  const pollIntervalRef = useRef(null);

  // ✅ Configuration API
  const API_URL = 'https://fbapi.tdidigital.io';
  const KIE_API_KEY = 'b58e2becd46da9228eb9a0c342080327';
  const KIE_API_URL = 'https://api.kie.ai';

  // ✅ Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      // FIX ESLint: Copier la ref dans une variable
      const currentAudios = audioRefs.current;
      Object.values(currentAudios).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);

  // ✅ NOUVEAU: Vérifier l'authentification
  const checkAuth = async () => {
    const token = localStorage.getItem('audio_token');
    if (token) {
      try {
        const response = await axios.post(`${API_URL}/api/audio/auth/verify`, { token });
        if (response.data.success) {
          setIsAuthenticated(true);
          setCurrentUser(response.data.user);
        } else {
          localStorage.removeItem('audio_token');
        }
      } catch (error) {
        localStorage.removeItem('audio_token');
      }
    }
  };

  // ✅ NOUVEAU: Gestion du login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/audio/auth/login`, loginForm);
      if (response.data.success) {
        localStorage.setItem('audio_token', response.data.token);
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
      }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Erreur de connexion');
    }
  };

  // ✅ NOUVEAU: Déconnexion
  const handleLogout = () => {
    const token = localStorage.getItem('audio_token');
    axios.post(`${API_URL}/api/audio/auth/logout`, { token });
    localStorage.removeItem('audio_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const pollTaskStatus = async (taskId) => {
    let attempts = 0;
    const maxAttempts = 60;

    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      
      if (attempts > maxAttempts) {
        clearInterval(pollIntervalRef.current);
        setLoading(false);
        setError('Timeout: La génération prend trop de temps');
        
        // ✅ NOUVEAU: Notifier le backend de l'erreur
        if (generationId) {
          const token = localStorage.getItem('audio_token');
          try {
            await axios.post(
              `${API_URL}/api/audio/generate/error`,
              { generation_id: generationId, error: 'Timeout' },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
          } catch (err) {
            console.error('Failed to log timeout:', err);
          }
        }
        return;
      }

      try {
        const response = await axios.get(`${KIE_API_URL}/api/v1/generate/record-info`, {
          headers: { 'Authorization': `Bearer ${KIE_API_KEY}` },
          params: { taskId }
        });

        const data = response.data;

        if (data.code === 200 && data.data) {
          const taskData = data.data;
          const status = taskData.status;

          if (status === 'PENDING' || status === 'PROCESSING') {
            const currentProgress = Math.min(30 + (attempts * 2), 95);
            setProgress(currentProgress);
          } 
          else if (status === 'SUCCESS' && taskData.response?.sunoData) {
            const tracks = taskData.response.sunoData;
            
            const normalizedTracks = tracks.map(track => ({
              id: track.id,
              audio_url: track.audioUrl || track.audio_url,
              stream_audio_url: track.streamAudioUrl || track.stream_audio_url,
              image_url: track.imageUrl || track.image_url,
              title: track.title,
              tags: track.tags,
              model_name: track.modelName || track.model_name || 'Suno AI',
              duration: track.duration,
              create_time: track.createTime || track.create_time
            }));

            clearInterval(pollIntervalRef.current);
            setProgress(100);
            
            // ✅ NOUVEAU: Calculer le temps de génération
            const generationTime = generationStartTime 
              ? (Date.now() - generationStartTime) / 1000 
              : 0;
            
            setResult({
              taskId,
              tracks: normalizedTracks,
              originalPrompt: formData.prompt,
              style: formData.useCustomStyle && formData.customStyle ? formData.customStyle : formData.style,
              title: formData.title,
              instrumental: formData.instrumental,
              model: formData.model
            });
            setLoading(false);
            
            // ✅ NOUVEAU: Notifier le backend que c'est terminé
            if (generationId) {
              const token = localStorage.getItem('audio_token');
              try {
                await axios.post(
                  `${API_URL}/api/audio/generate/complete`,
                  {
                    generation_id: generationId,
                    task_id: taskId,
                    tracks: normalizedTracks,
                    generation_time: generationTime
                  },
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
              } catch (err) {
                console.error('Failed to complete generation tracking:', err);
              }
            }
          }
          else if (status === 'FAILED') {
            clearInterval(pollIntervalRef.current);
            setLoading(false);
            const errorMsg = taskData.errorMessage || 'La génération a échoué';
            setError(errorMsg);
            
            // ✅ NOUVEAU: Notifier le backend de l'erreur
            if (generationId) {
              const token = localStorage.getItem('audio_token');
              try {
                await axios.post(
                  `${API_URL}/api/audio/generate/error`,
                  { generation_id: generationId, error: errorMsg },
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
              } catch (err) {
                console.error('Failed to log error:', err);
              }
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
  };

  const handleGenerate = async () => {
    if (!formData.prompt.trim()) {
      setError('Veuillez entrer une description');
      return;
    }

    if (formData.customMode && !formData.title.trim()) {
      setError('Veuillez entrer un titre');
      return;
    }

    const finalStyle = formData.useCustomStyle && formData.customStyle.trim() 
      ? formData.customStyle.trim() 
      : formData.style;

    if (!finalStyle) {
      setError('Veuillez choisir un style musical');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(10);
    setResult(null);
    setGenerationStartTime(Date.now());

    // ✅ NOUVEAU: Créer le record de génération dans le backend
    const token = localStorage.getItem('audio_token');
    try {
      const startResponse = await axios.post(
        `${API_URL}/api/audio/generate/start`,
        {
          user_email: currentUser?.email || 'unknown@email.com',
          prompt: formData.prompt,
          style: finalStyle,
          title: formData.title || 'Generated Audio',
          model: formData.model,
          instrumental: formData.instrumental,
          customMode: formData.customMode
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setGenerationId(startResponse.data.generation_id);
    } catch (err) {
      console.error('Failed to start generation tracking:', err);
    }

    try {
      const payload = {
        prompt: formData.prompt,
        style: finalStyle,
        title: formData.title || 'Generated Audio',
        customMode: formData.customMode,
        instrumental: formData.instrumental,
        model: formData.model,
        callBackUrl: 'https://webhook.site/your-callback-endpoint'
      };

      const response = await axios.post(
        `${KIE_API_URL}/api/v1/generate`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${KIE_API_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.code === 200 && response.data.data.taskId) {
        setProgress(20);
        pollTaskStatus(response.data.data.taskId);
      } else {
        throw new Error(response.data.msg || 'Erreur lors de la génération');
      }

    } catch (err) {
      setLoading(false);
      
      let errorMessage = 'Erreur lors de la génération';
      
      if (err.response?.data?.msg) {
        errorMessage = err.response.data.msg;
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Clé API invalide ou permissions manquantes';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // ✅ NOUVEAU: Notifier le backend de l'erreur
      if (generationId) {
        const token = localStorage.getItem('audio_token');
        try {
          await axios.post(
            `${API_URL}/api/audio/generate/error`,
            { generation_id: generationId, error: errorMessage },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (err) {
          console.error('Failed to log error:', err);
        }
      }
    }
  };

  const togglePlayPause = (trackId, audioUrl) => {
    const audio = audioRefs.current[trackId];

    if (!audio) {
      const newAudio = new Audio(audioUrl);
      audioRefs.current[trackId] = newAudio;
      
      newAudio.addEventListener('ended', () => {
        setIsPlaying(prev => ({ ...prev, [trackId]: false }));
      });

      newAudio.play();
      setIsPlaying(prev => ({ ...prev, [trackId]: true }));
    } else {
      if (isPlaying[trackId]) {
        audio.pause();
        setIsPlaying(prev => ({ ...prev, [trackId]: false }));
      } else {
        audio.play();
        setIsPlaying(prev => ({ ...prev, [trackId]: true }));
      }
    }
  };

  const resetForm = () => {
    setResult(null);
    setError(null);
    setProgress(0);
    setGenerationId(null);
    setGenerationStartTime(null);
    setFormData({
      prompt: '',
      style: 'Pop',
      customStyle: '',
      useCustomStyle: false,
      title: '',
      customMode: true,
      instrumental: false,
      model: 'V5'
    });
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
  };

  const getProgressStage = () => {
    if (progress < 20) return 'Envoi de la requête...';
    if (progress < 40) return 'Génération du texte...';
    if (progress < 70) return `Création de l'audio...`; // FIX ESLint: backticks
    if (progress < 95) return 'Finalisation...';
    return 'Terminé!';
  };

  // ✅ NOUVEAU: Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black flex items-center justify-center p-4">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full mb-6 relative">
              <Lock className="w-12 h-12 text-white" />
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-xl opacity-50 animate-pulse" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                Audio Generator
              </span>
            </h1>
            <p className="text-gray-300">Connectez-vous pour accéder à la plateforme</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
            <div className="space-y-6">
              <div>
                <label className="block text-white text-sm font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => {
                    setLoginForm({ ...loginForm, email: e.target.value });
                    setLoginError('');
                  }}
                  placeholder="votre@email.com"
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-white text-sm font-semibold mb-2">Mot de passe</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => {
                    setLoginForm({ ...loginForm, password: e.target.value });
                    setLoginError('');
                  }}
                  placeholder="••••••••"
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 transition-all"
                  required
                />
              </div>

              {loginError && (
                <div className="flex items-center justify-center space-x-2 text-red-400 bg-red-500/20 border border-red-500/50 rounded-xl p-3">
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm">{loginError}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all transform shadow-2xl shadow-indigo-500/50 flex items-center justify-center space-x-2"
              >
                <Lock className="w-5 h-5" />
                <span>Se connecter</span>
              </button>
            </div>
          </form>

          <div className="mt-8 text-center">
            <a
              href="/"
              className="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Main Audio Generator Interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black pt-20 pb-20">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 8px;
        }
        .scrollbar-thumb-purple-500\\/50::-webkit-scrollbar-thumb {
          background-color: rgba(168, 85, 247, 0.5);
          border-radius: 4px;
        }
        .scrollbar-track-transparent::-webkit-scrollbar-track {
          background-color: transparent;
        }
      `}</style>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-between mb-6">
            <a
              href="/"
              className="text-white/70 hover:text-white transition-colors text-sm inline-flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </a>
            
            {/* ✅ NOUVEAU: Affichage du user + déconnexion */}
            <div className="flex items-center space-x-4">
              {currentUser && (
                <span className="text-white/70 text-sm">
                  👤 {currentUser.name || currentUser.email}
                </span>
              )}
              <button
                onClick={handleLogout}
                className="text-white/70 hover:text-white transition-colors text-sm flex items-center space-x-1"
              >
                <Lock className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>

          <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 rounded-full backdrop-blur-sm">
            <span className="text-indigo-300 font-semibold flex items-center space-x-2">
              <Sparkles className="w-4 h-4" />
              <span>Powered by TeckiA</span>
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
              Audio Generator
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Créez de la musique professionnelle avec l'IA la plus avancée
          </p>
        </div>

        {/* Form */}
        {!result && !loading && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <div className="space-y-6">
                {/* Prompt */}
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Description / Paroles {!formData.customMode && '*'}
                  </label>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    placeholder={formData.customMode && formData.instrumental 
                      ? "Description de la musique (optionnel en mode instrumental)"
                      : "Décrivez la musique que vous voulez générer ou entrez les paroles"
                    }
                    rows={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 resize-none"
                  />
                  <p className="text-gray-400 text-sm mt-2">
                    {formData.customMode ? 'Max 5000 caractères' : 'Max 500 caractères'}
                  </p>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Titre {formData.customMode && '*'}
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Mon Audio Incroyable"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
                    maxLength={80}
                  />
                </div>

                {/* Style */}
                <div>
                  <label className="block text-white font-semibold mb-2">
                    Style Musical {formData.customMode && '*'}
                  </label>
                  
                  {!formData.useCustomStyle ? (
                    <div className="flex gap-2">
                      <select
                        value={formData.style}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setFormData({ ...formData, useCustomStyle: true, style: '' });
                          } else {
                            setFormData({ ...formData, style: e.target.value });
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-black focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
                      >
                        <option value="Pop">Pop</option>
                        <option value="Rock">Rock</option>
                        <option value="Jazz">Jazz</option>
                        <option value="Classical">Classical</option>
                        <option value="Electronic">Electronic</option>
                        <option value="Hip-hop">Hip-hop</option>
                        <option value="Country">Country</option>
                        <option value="R&B">R&B</option>
                        <option value="Metal">Metal</option>
                        <option value="Reggae">Reggae</option>
                        <option value="Blues">Blues</option>
                        <option value="Folk">Folk</option>
                        <option value="Latin">Latin</option>
                        <option value="Soul">Soul</option>
                        <option value="Funk">Funk</option>
                        <option value="custom">✨ Autre (personnalisé)</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={formData.customStyle}
                        onChange={(e) => setFormData({ ...formData, customStyle: e.target.value })}
                        placeholder="Ex: Afrobeat fusion with jazz elements"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
                        maxLength={1000}
                      />
                      <button
                        onClick={() => setFormData({ ...formData, useCustomStyle: false, customStyle: '', style: 'Pop' })}
                        className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        ← Retour aux styles prédéfinis
                      </button>
                    </div>
                  )}
                  
                  <p className="text-gray-400 text-sm mt-2">
                    Vous pouvez mélanger plusieurs styles (ex: "Jazz Rock with Electronic vibes")
                  </p>
                </div>

                {/* Options */}
                <div className="grid md:grid-cols-3 gap-4">
                  <label className="flex items-center space-x-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.customMode}
                      onChange={(e) => setFormData({ ...formData, customMode: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-white font-semibold">Mode Personnalisé</p>
                      <p className="text-gray-400 text-xs">Plus de contrôle</p>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.instrumental}
                      onChange={(e) => setFormData({ ...formData, instrumental: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <p className="text-white font-semibold">Instrumental</p>
                      <p className="text-gray-400 text-xs">Sans paroles</p>
                    </div>
                  </label>

                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Modèle</label>
                    <select
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-black focus:outline-none focus:border-indigo-500 text-sm"
                    >
                      <option value="V5">V5 (Meilleur)</option>
                      <option value="V4_5PLUS">V4.5 Plus</option>
                      <option value="V4_5">V4.5</option>
                      <option value="V4">V4</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-12 py-5 rounded-2xl font-bold text-xl hover:scale-105 transition-all transform shadow-2xl shadow-indigo-500/50 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Wand2 className="w-6 h-6 group-hover:rotate-12 transition-transform duration-500" />
                <span>Générer l'Audio</span>
              </button>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-red-200 font-semibold">Erreur</p>
                    <p className="text-red-300 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10">
              <div className="text-center space-y-6">
                <div className="relative inline-block">
                  <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center animate-pulse">
                    <Loader2 className="w-16 h-16 text-white animate-spin" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-2xl opacity-50 animate-ping" />
                </div>

                <div>
                  <p className="text-white text-2xl font-bold mb-2">Génération en cours...</p>
                  <p className="text-indigo-300 text-lg">{getProgressStage()}</p>
                </div>

                <div className="w-full bg-gray-700/50 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-500 relative overflow-hidden"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/30 animate-pulse" />
                  </div>
                </div>
                <p className="text-gray-400 text-sm">{Math.round(progress)}% - Cela peut prendre 1-3 minutes...</p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-3xl p-8 backdrop-blur-sm">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-2">Génération terminée!</h2>
              <p className="text-gray-300">{result.tracks.length} piste(s) générée(s)</p>
            </div>

            {/* Lyrics Display */}
            {result.originalPrompt && (
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-3xl p-8 border border-purple-500/30">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Music className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Paroles / Description</h3>
                      <p className="text-purple-300 text-sm">Cliquez pour copier</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(result.originalPrompt);
                      const toast = document.createElement('div');
                      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-2xl z-50 flex items-center space-x-2 animate-fade-in';
                      toast.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span>Copié!</span>';
                      document.body.appendChild(toast);
                      setTimeout(() => toast.remove(), 2000);
                    }}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all backdrop-blur-sm border border-white/20 flex items-center space-x-2 group"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copier</span>
                  </button>
                </div>
                
                <div 
                  onClick={() => {
                    navigator.clipboard.writeText(result.originalPrompt);
                    const toast = document.createElement('div');
                    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-2xl z-50 flex items-center space-x-2 animate-fade-in';
                    toast.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span>Copié!</span>';
                    document.body.appendChild(toast);
                    setTimeout(() => toast.remove(), 2000);
                  }}
                  className="relative group cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
                  <div className="relative bg-black/40 backdrop-blur-sm rounded-2xl p-6 border border-white/10 group-hover:border-purple-500/50 transition-all">
                    <pre className="text-white/90 whitespace-pre-wrap font-mono text-sm leading-relaxed max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-transparent">
{result.originalPrompt}
                    </pre>
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="bg-purple-500/90 backdrop-blur-sm px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-xl">
                      Cliquez pour copier
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <div className="px-4 py-2 bg-white/5 rounded-xl border border-white/10">
                    <span className="text-purple-300 text-sm">Style: </span>
                    <span className="text-white font-semibold">{result.style}</span>
                  </div>
                  <div className="px-4 py-2 bg-white/5 rounded-xl border border-white/10">
                    <span className="text-purple-300 text-sm">Titre: </span>
                    <span className="text-white font-semibold">{result.title}</span>
                  </div>
                  {result.instrumental && (
                    <div className="px-4 py-2 bg-purple-500/20 rounded-xl border border-purple-500/30">
                      <span className="text-purple-200 font-semibold">🎹 Instrumental</span>
                    </div>
                  )}
                  <div className="px-4 py-2 bg-white/5 rounded-xl border border-white/10">
                    <span className="text-purple-300 text-sm">Modèle: </span>
                    <span className="text-white font-semibold">{result.model}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Audio Tracks */}
            <div className="grid md:grid-cols-2 gap-6">
              {result.tracks.map((track) => (
                <div key={track.id} className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/10 hover:border-indigo-500/50 transition-all">
                  <div className="relative mb-4 rounded-xl overflow-hidden aspect-square">
                    <img
                      src={track.image_url}
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <button
                      onClick={() => togglePlayPause(track.id, track.audio_url)}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all">
                        {isPlaying[track.id] ? (
                          <Pause className="w-8 h-8 text-white" />
                        ) : (
                          <Play className="w-8 h-8 text-white ml-1" />
                        )}
                      </div>
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{track.title}</h3>
                      <p className="text-gray-400 text-sm">{track.tags}</p>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <span className="flex items-center space-x-1">
                        <Volume2 className="w-4 h-4" />
                        <span>{Math.round(track.duration)}s</span>
                      </span>
                      <span>{track.model_name}</span>
                    </div>

                    <a
                      href={track.audio_url}
                      download={`${track.title || 'audio'}.mp3`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                    >
                      <Download className="w-5 h-5" />
                      <span>Télécharger</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={resetForm}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all backdrop-blur-sm border border-white/20"
              >
                Nouvelle Génération
              </button>
            </div>

            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-amber-300 font-bold mb-1">💡 Astuce</p>
                  <p className="text-gray-300">Les fichiers sont disponibles pendant 14 jours. Téléchargez-les rapidement!</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioGenerator;